import { Fragment, useCallback, useMemo } from "react";
import { Select } from "../../components";
import { supportsZones } from "../../utils";
import { useFormatMessage } from "../../i18n";

export function ControlSelect({
  controls = [],
  formik,
  disabled,
  deviceSelected,
  loading,
}) {
  const tr = useFormatMessage();
  const hasZoneSupport = supportsZones(deviceSelected);

  const filterControlsBySelectedType = useCallback(
    (controls) => {
      return controls.filter((control) => {
        return control.possible_types.includes(parseInt(formik.values.type));
      });
    },
    [formik.values.type]
  );

  const filterControlsByZonesAndSections = useCallback(
    (controls, zone, section) => {
      return controls.filter((control) =>
        hasZoneSupport
          ? control.zoneId === zone.id && control.sectionId === section.id
          : control.sectionId === section.id
      );
    },
    [hasZoneSupport]
  );

  function mapZonesWithControls() {
    const selectedTypeControls = filterControlsBySelectedType(controls);
    const zones = deviceSelected?.zones.map((zone) => {
      return {
        ...zone,
        sections: zone.sections.map((section) => {
          return {
            ...section,
            controls: filterControlsByZonesAndSections(
              selectedTypeControls,
              zone,
              section
            ),
          };
        }),
      };
    });
    return zones;
  }

  const zonesWithControls = useMemo(mapZonesWithControls, [
    controls,
    deviceSelected?.zones,
    filterControlsBySelectedType,
    filterControlsByZonesAndSections,
  ]);

  function getTagFromControlId(controlId) {
    const tags = {
      l: "left",
      r: "right",
    };
    const controlIdTag = controlId.slice(-1);
    return tags[controlIdTag];
  }

  function mapControlsIntoOptions() {
    const showZonesOptGroup = hasZoneSupport;
    const controlsOptions = zonesWithControls?.map((zone) => {
      return (
        zone.sections.find(({ controls }) => controls.length) && (
          <Fragment key={zone.id || "zone"}>
            {showZonesOptGroup && <optgroup label={zone.name || zone.id} />}
            {zone.sections.map((section) => {
              return (
                section.controls.length && (
                  <optgroup
                    label={`${showZonesOptGroup ? "\u00A0\u00A0" : ""} ${tr("page")} ${section.name || section.id}`}
                    key={`${zone.id}-${section.id}`}
                  >
                    {section.controls.map((control) => {
                      const controlTag = getTagFromControlId(control.id);
                      return (
                        <option
                          value={JSON.stringify(control)}
                          key={control.id}
                        >
                          {`${control.name}${controlTag ? ` - ${tr(controlTag)}` : ""}`}
                        </option>
                      );
                    })}
                  </optgroup>
                )
              );
            })}
          </Fragment>
        )
      );
    });
    return controlsOptions;
  }
  const availableOptions = useMemo(mapControlsIntoOptions, [
    tr,
    zonesWithControls,
    hasZoneSupport,
  ]);

  return (
    <Select
      name="control"
      onChange={formik.handleChange}
      options={availableOptions}
      optionsState={formik.values.control}
      loading={loading}
      disabled={disabled}
    />
  );
}
